<template>
    <AppLayout>
        <template #icon>
            <div class="flex h-10 w-10 items-center justify-center">
                <svg
                    width="26"
                    height="30"
                    viewBox="0 0 26 30"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                >
                    <path
                        d="M25 14.3333V10.7333C25 8.49311 25 7.37301 24.564 6.51736C24.1805 5.76471 23.5686 5.15279 22.816 4.7693C21.9603 4.33332 20.8402 4.33332 18.6 4.33332H7.4C5.15979 4.33332 4.03969 4.33332 3.18404 4.7693C2.43139 5.15279 1.81947 5.76471 1.43597 6.51736C1 7.37301 1 8.49311 1 10.7333V21.9333C1 24.1735 1 25.2936 1.43597 26.1493C1.81947 26.9019 2.43139 27.5139 3.18404 27.8973C4.03969 28.3333 5.15979 28.3333 7.4 28.3333H13.6667M25 12.3333H1M18.3333 1.66666V6.99999M7.66667 1.66666V6.99999M21 27V19M17 23H25"
                        stroke="url(#paint0_linear_123_247)"
                        stroke-width="1.8"
                        stroke-linecap="round"
                        stroke-linejoin="round"
                    />
                    <defs>
                        <linearGradient
                            id="paint0_linear_123_247"
                            x1="3.60632"
                            y1="4.33394"
                            x2="24.0033"
                            y2="24.135"
                            gradientUnits="userSpaceOnUse"
                        >
                            <stop stop-color="#FF8D47" />
                            <stop offset="0.147634" stop-color="#FF7447" />
                            <stop offset="0.454086" stop-color="#FF4247" />
                            <stop offset="0.636926" stop-color="#E92A5B" />
                            <stop offset="0.740367" stop-color="#DD1C67" />
                            <stop offset="0.842991" stop-color="#D31071" />
                            <stop offset="1" stop-color="#CF0283" />
                        </linearGradient>
                    </defs>
                </svg>
            </div>
        </template>
        <template #title> {{ $t("bookings.title") }} </template>
        <template #description> {{ $t("bookings.description") }} </template>
        <div class="pb-20">
            <div>
                <div>
                    <div class="p-6">
                        <div class="flex items-center justify-between">
                            <div class="block">
                                <div>
                                    <nav class="flex gap-x-2" aria-label="Tabs">
                                        <inertia-link
                                            v-for="tab in tabs"
                                            :key="tab.name"
                                            :href="tab.href"
                                            :class="[
                                                'inline-flex items-center px-3 py-2 text-center text-sm font-medium',
                                                tab.current
                                                    ? ' rounded-lg bg-iron-200 text-center text-iron-700 '
                                                    : 'text-iron-500 hover:border-iron-300 hover:text-iron-700  ',
                                            ]"
                                        >
                                            {{ tab.name }}
                                            <span
                                                class="ml-2 rounded px-[5px] text-xs leading-[18px]"
                                                :class="[
                                                    tab.current
                                                        ? 'bg-iron-700 text-white'
                                                        : 'bg-iron-200 text-iron-700',
                                                ]"
                                                >{{ tab.count }}</span
                                            >
                                        </inertia-link>
                                    </nav>
                                </div>
                            </div>

                            <div v-if="teams.length > 0" class="flex gap-2">
                                <Select
                                    :model-value="selectedTeamId"
                                    @update:model-value="showByTeam"
                                >
                                    <SelectTrigger>
                                        <SelectValue
                                            :placeholder="
                                                $t('bookings.by_team')
                                            "
                                        />
                                    </SelectTrigger>
                                    <SelectContent>
                                        <SelectGroup>
                                            <SelectItem
                                                class="px-4"
                                                v-for="team of teams"
                                                :key="team.id"
                                                :value="team.id.toString()"
                                            >
                                                {{ team.name }}
                                            </SelectItem>
                                        </SelectGroup>
                                    </SelectContent>
                                </Select>

                                <Button
                                    @click="cancelTeamFilter"
                                    v-if="selectedTeamId"
                                    variant="outline"
                                    ><X :size="16"
                                /></Button>
                            </div>
                        </div>

                        <div
                            v-if="reservationDateGroups.length !== 0"
                            class="pt-6"
                        >
                            <div v-auto-animate>
                                <div
                                    v-for="(
                                        reservations, date
                                    ) in reservationDateGroups"
                                    :key="date"
                                    class="mb-4"
                                >
                                    <Event
                                        v-for="reservation in reservations"
                                        :key="reservation.id"
                                        class="my-4"
                                        :reservation="reservation"
                                    />
                                </div>
                            </div>
                        </div>
                        <div
                            v-else
                            class="flex w-full items-center justify-center py-12"
                        >
                            <EmptyStateWithIcon
                                :title="$t('bookings.empty_state.title')"
                                :description="
                                    $t('bookings.empty_state.description')
                                "
                            >
                                <template #icon>
                                    <svg
                                        width="76"
                                        height="76"
                                        viewBox="0 0 76 76"
                                        fill="none"
                                        xmlns="http://www.w3.org/2000/svg"
                                    >
                                        <path
                                            d="M22.0219 14.1338L24.2397 24.5674M42.8891 9.6984L45.1068 20.132M62.5842 38.227L58.1488 17.3598C57.8547 15.9762 57.023 14.7662 55.8367 13.9958C54.6505 13.2254 53.2067 12.9578 51.8231 13.2519L15.3056 21.0139C13.9221 21.308 12.712 22.1397 11.9416 23.326C11.1712 24.5123 10.9036 25.956 11.1977 27.3396L18.9597 63.8571C19.2538 65.2407 20.0855 66.4507 21.2718 67.2211C22.4581 67.9915 23.9018 68.2591 25.2854 67.965L46.1525 63.5296M13.4154 37.7732L60.3665 27.7934M52.8689 56.6495L59.1945 60.7574L67.4104 48.1061"
                                            stroke="url(#paint0_linear_620_2321)"
                                            stroke-width="4"
                                            stroke-linecap="round"
                                            stroke-linejoin="round"
                                        />
                                        <defs>
                                            <linearGradient
                                                id="paint0_linear_620_2321"
                                                x1="15.4711"
                                                y1="20.98"
                                                x2="64.0716"
                                                y2="53.3919"
                                                gradientUnits="userSpaceOnUse"
                                            >
                                                <stop stop-color="#FF9B47" />
                                                <stop
                                                    offset="0.147634"
                                                    stop-color="#FF7447"
                                                />
                                                <stop
                                                    offset="0.454086"
                                                    stop-color="#FF4247"
                                                />
                                                <stop
                                                    offset="0.636926"
                                                    stop-color="#E92A5B"
                                                />
                                                <stop
                                                    offset="0.740367"
                                                    stop-color="#DD1C67"
                                                />
                                                <stop
                                                    offset="0.842991"
                                                    stop-color="#D31071"
                                                />
                                                <stop
                                                    offset="1"
                                                    stop-color="#CF0283"
                                                />
                                            </linearGradient>
                                        </defs>
                                    </svg>
                                </template>
                            </EmptyStateWithIcon>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    </AppLayout>
</template>

<script setup lang="ts">
import AppLayout from "../../../../Layouts/AppLayout.vue";
import { ref, computed } from "vue";
import route from "ziggy-js";
import Event from "../../../../Pages/ZapTime/App/Dashboard/components/event.vue";
import { useI18n } from "vue-i18n";
import { EmptyStateWithIcon } from "@/components/empty-state-with-icon";
import { router, usePage } from "@inertiajs/vue3";
import { Button } from "@/components/ui/button";
import {
    Select,
    SelectContent,
    SelectGroup,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from "@/components/ui/select";
import { X } from "lucide-vue-next";
import { vAutoAnimate } from "@formkit/auto-animate/vue";

const page = usePage();

const props = defineProps({
    reservationDateGroups: {
        type: Object,
        required: true,
    },
    upcomingReservationsCount: {
        type: Number,
        required: true,
    },
    cancelledReservationsCount: {
        type: Number,
        required: true,
    },
    pastReservationsCount: {
        type: Number,
        required: true,
    },
});

const teams = computed(() => {
    //@ts-expect-error
    return page.props.allTeams.filter(
        (team: any) => team.personal_team === false,
    );
});

const { t } = useI18n();

const selectedTeamId = ref();

function showByTeam(id: string) {
    selectedTeamId.value = id;
    router.visit(route("app.dashboard", { teamId: id }), {
        preserveState: true,
    });
}

function cancelTeamFilter() {
    selectedTeamId.value = undefined;
    router.visit(route("app.dashboard", { teamId: undefined }), {
        preserveState: true,
    });
}

const tabs = ref([
    {
        name: t("bookings.upcoming"),
        href: route("app.dashboard.upcoming"),
        current:
            route().current("app.dashboard") ||
            route().current("app.dashboard.upcoming"),
        count: props.upcomingReservationsCount,
    },
    // {
    //     name: t("bookings.pending"),
    //     href: "#",
    //     current: false,
    // },
    {
        name: t("bookings.past"),
        href: route("app.dashboard.past"),
        current: route().current("app.dashboard.past"),
        count: props.pastReservationsCount,
    },
    {
        name: t("bookings.cancelled"),
        href: route("app.dashboard.cancelled"),
        current: route().current("app.dashboard.cancelled"),
        count: props.cancelledReservationsCount,
    },
]);
</script>
